import {Router} from '@angular/router';
import {SimpleChanges} from '@angular/core';

import {
    ReportColumnNames,
    ReportExecutionColumnNames,
    ReportExtensions,
    ReportRowType
} from '@spa/api';

import {Helper} from '../../../shared/classes';
import {ColumnSizesEnum} from '../../../shared/enums/common';
import {CacheCollectionService} from '../../../shared/services';

import {
    ReportTableData,
    ReportTableColumn,
    ReportTableDataRow,
    ReportTableGroupedColumn,
    ReportTableConfigInterface,
    ReportTableDataRowInterface,
} from '../../../shared/components';

import {BaseComponent} from './base-component';

import {EventsService} from '../services';
import {LayoutConfigInterface} from '../components';

export abstract class Report<ReportItemType extends ReportRowType> extends BaseComponent {
    protected router: Router;
    protected cacheCollectionService: CacheCollectionService;

    public eventsService: EventsService;
    public dataTableConfig: ReportTableConfigInterface;

    protected constructor(
        eventsService: EventsService,
        router?: Router,
        cacheCollectionService?: CacheCollectionService
    ) {
        super(eventsService, router, cacheCollectionService);
    }

    private getReportTableData(
        colId: string,
        reportItem: ReportItemType): ReportTableData {
        const feesSelectors: string[] = [
            ReportExecutionColumnNames.Commission,
            ReportExecutionColumnNames.Ecn,
            ReportExecutionColumnNames.Sec,
            ReportExecutionColumnNames.Taf,
            ReportExecutionColumnNames.Nscc,
            ReportExecutionColumnNames.Clr,
            ReportExecutionColumnNames.Orf,
            ReportExecutionColumnNames.Ptfpf,
            ReportExecutionColumnNames.Eftt,
            ReportExecutionColumnNames.Cat,
            ReportExecutionColumnNames.Acc,
            ReportExecutionColumnNames.Contra
        ];

        const isFeeSelector = feesSelectors.indexOf(colId) >= 0;

        const value = !isFeeSelector ?
            this.mapDataTableItem(colId, reportItem, false) :
            this.getFeesDataTableItemValue(colId, reportItem);

        return new ReportTableData({
            colId,
            value,
            hidden: false,
            shifted: this.isShiftedTableData(colId),
            hasNegativeValue: !this.isShiftedTableData(colId) ? false : Helper.hasNegativeValue(value),
            class: colId === ReportExecutionColumnNames.Symbol ? "symbol-cell" : null
        });
    }

    protected getFeesDataTableItemValue(colId: string, reportItem: ReportRowType): string {
        if (!reportItem || !reportItem.hasOwnProperty(colId)) {
            return '';
        }

        const value = reportItem[colId] as number;

        return ReportExtensions.toDecimalString(value, 6);
    }

    abstract getReportTableConfig(): ReportTableConfigInterface;

    abstract getReportData(): ReportItemType[];

    abstract mapDataTableItem(colId: string, reportItem: ReportItemType, isTotalRow: boolean): string | number;

    abstract isShiftedTableData(colId: string): boolean;

    getLayoutConfig(): LayoutConfigInterface {
        return {
            mainColumnSize: ColumnSizesEnum.Col12,
            subColumnSize: null
        };
    }

    getText(): string {
        return '';
    }

    getTitle(): string {
        return '';
    }

    getTableData(): ReportTableDataRow[] {
        return this.getReportData().map((reportItem: ReportItemType, index: number) => {
            const row: ReportTableDataRowInterface = {rowId: index, dataItems: []};

            this.dataTableConfig.columns.forEach((column: ReportTableColumn | ReportTableGroupedColumn) => {
                if (column instanceof ReportTableGroupedColumn) {
                    column.childColumns.forEach((childColumn: ReportTableColumn) => {
                        row.dataItems.push(this.getReportTableData(childColumn.selector, reportItem));
                    });
                } else {
                    row.dataItems.push(this.getReportTableData(column.selector, reportItem));
                }
            });

            return new ReportTableDataRow(row);
        });
    }

    onInit(): void {
        this.dataTableConfig = this.getReportTableConfig();

        this.dataTableConfig.rows = this.getTableData();
    }

    onChanges(changes: SimpleChanges): void {
        Object.keys(changes).map((key: string) => {
            if (!changes[key].firstChange) {
                this.dataTableConfig.rows = [];

                this.dataTableConfig.rows = this.getTableData();
            }
        });
    }
}
